import { AssessmentKeys, CamelCaseKeys, Form, FormsKeys, FormSubmission } from '@uplift/types';

import { restClient } from '../../client';
import { Appointment, Match, SessionNotes, User, UserProfile } from '../../resources';
import { CareType } from '../higherEd';
import { MedicalNoteActivity, MedicalNoteType } from '../medical-notes';
import { PatientStaticDocument, PatientUploadedMedicalDocument } from '../patientDocuments';

export enum PatientActivityType {
  newMatchByAdmin = 'new_match_by_admin',
  newMatchByReferral = 'new_match_by_referral',
  newMatchByRematch = 'new_match_by_rematch',
  newMatchByPsychiatryBooking = 'new_match_by_psychiatry_booking',
  rematched = 'rematched',

  formInitial = 'form_initial',
  formExpired = 'form_expired',
  formCompleted = 'form_completed',

  assessmentFormInitial = 'assessment_form_initial',
  assessmentFormExpired = 'assessment_form_expired',
  assessmentFormCompleted = 'assessment_form_completed',

  therapySessionInitial = 'therapy_session_initial',
  therapySessionSubmitted = 'therapy_session_submitted',
  therapySessionCompleted = 'therapy_session_completed',

  psychiatrySessionInitial = 'psychiatry_session_initial',
  psychiatrySessionSubmitted = 'psychiatry_session_submitted',
  psychiatrySessionCompleted = 'psychiatry_session_completed',
  psychiatrySessionCompletedOnCanvas = 'psychiatry_session_completed_on_canvas',

  document = 'document',

  noteInitial = 'note_initial',
  noteSubmitted = 'note_submitted',

  referral = 'referral',
}

export type PatientFormActivity = CamelCaseKeys<{
  activity_type: PatientActivityType.formInitial | PatientActivityType.formExpired | PatientActivityType.formCompleted;
  datetime: string;
  form_key?: FormsKeys;
  form: Form;
  submission: FormSubmission;

  details?: {
    reasons_for_visit: string;
  };
}>;


export type PatientAssessmentFormActivity = CamelCaseKeys<{
  activity_type: PatientActivityType.assessmentFormInitial | PatientActivityType.assessmentFormExpired | PatientActivityType.assessmentFormCompleted;
  datetime: string;
  form_key: AssessmentKeys;
  form: Form;
  submission: FormSubmission;
  score: number;
  score_diff_percentage?: number;
}>;


export type PatientTherapyAppointmentActivity = CamelCaseKeys<{
  activity_type: PatientActivityType.therapySessionInitial | PatientActivityType.therapySessionCompleted | PatientActivityType.therapySessionSubmitted;
  datetime: string;
  is_first_session: boolean;
  clinical_notes: SessionNotes;
  appointment: Appointment;
  provider: User;
}>;

export type PatientPsychiatryAppointmentActivity = CamelCaseKeys<{
  activity_type: PatientActivityType.psychiatrySessionInitial | PatientActivityType.psychiatrySessionCompleted | PatientActivityType.psychiatrySessionCompletedOnCanvas | PatientActivityType.psychiatrySessionSubmitted;
  datetime: string;
  is_first_session: boolean;
  appointment: Appointment;
  provider: User;
  plan?: {
    treatment_plan_continue?: string;
    treatment_plan_start?: string;
    treatment_plan_stop?: string;
    treatment_plan_change?: string;
  };
}>;

export type PatientMatchActivity = CamelCaseKeys<{
  activity_type: (
    | PatientActivityType.rematched
    | PatientActivityType.newMatchByAdmin
    | PatientActivityType.newMatchByReferral
    | PatientActivityType.newMatchByRematch
    | PatientActivityType.newMatchByPsychiatryBooking
  );
  datetime: string;
  match: Match;
  fromUser?: {
    user: User;
    profile: UserProfile;
  };
}>;

export type PatientDocumentActivity = CamelCaseKeys<{
  activity_type: PatientActivityType.document;
  datetime: string;
  document: PatientStaticDocument | PatientUploadedMedicalDocument;
}>;

export type PatientNoteActivity = CamelCaseKeys<{
  activity_type: (
    | PatientActivityType.noteInitial
    | PatientActivityType.noteSubmitted
  );
  datetime: string;
  note: MedicalNoteActivity;
  noteType: MedicalNoteType;
  provider: User;
  patient: User;
}>;

export type PartnerReferralActivity = {
  activityType: PatientActivityType.referral;
  datetime: string;
  referral: {
    careType: CareType;
    content: string;
    createdAt: string;
    updatedAt: string;
  };
  fromUser: {
    user: User;
    profile: UserProfile;
  };
  aboutUser: {
    user: User;
    profile: UserProfile;
  };
  forUser?: {
    user: User;
    profile: UserProfile;
  };
};

export type PatientActivity =
  | PatientFormActivity
  | PatientAssessmentFormActivity
  | PatientTherapyAppointmentActivity
  | PatientPsychiatryAppointmentActivity
  | PatientMatchActivity
  | PatientDocumentActivity
  | PatientNoteActivity
  | PartnerReferralActivity;

export type ActivityTypeGroups =
  | 'match'
  | 'form'
  | 'assessment'
  | 'therapySession'
  | 'psychiatrySession'
  | 'document'
  | 'note'
  | 'referral';

const activityTypeMap: Record<ActivityTypeGroups, PatientActivityType[]> = {
  match: [
    PatientActivityType.newMatchByAdmin,
    PatientActivityType.newMatchByReferral,
    PatientActivityType.newMatchByRematch,
    PatientActivityType.newMatchByPsychiatryBooking,
    PatientActivityType.rematched
  ],
  form: [
    PatientActivityType.formInitial,
    PatientActivityType.formExpired,
    PatientActivityType.formCompleted,
  ],
  assessment: [
    PatientActivityType.assessmentFormInitial,
    PatientActivityType.assessmentFormExpired,
    PatientActivityType.assessmentFormCompleted,
  ],
  therapySession: [
    PatientActivityType.therapySessionInitial,
    PatientActivityType.therapySessionSubmitted,
    PatientActivityType.therapySessionCompleted,
  ],
  psychiatrySession: [
    PatientActivityType.psychiatrySessionInitial,
    PatientActivityType.psychiatrySessionSubmitted,
    PatientActivityType.psychiatrySessionCompleted,
    PatientActivityType.psychiatrySessionCompletedOnCanvas,
  ],
  document: [
    PatientActivityType.document,
  ],
  note: [
    PatientActivityType.noteInitial,
    PatientActivityType.noteSubmitted,
  ],
  referral: [
    PatientActivityType.referral,
  ],
};

export const isPatientAssessmentFormActivity = (activity: PatientActivity): activity is PatientAssessmentFormActivity =>
  activityTypeMap.assessment.includes(activity.activityType);

export const isPatientDocumentActivity = (activity: PatientActivity): activity is PatientDocumentActivity =>
  activityTypeMap.document.includes(activity.activityType);

export const isPatientFormActivity = (activity: PatientActivity): activity is PatientFormActivity =>
  activityTypeMap.form.includes(activity.activityType);

export const isPatientMatchActivity = (activity: PatientActivity): activity is PatientMatchActivity =>
  activityTypeMap.match.includes(activity.activityType);

export const isPatientNoteActivity = (activity: PatientActivity): activity is PatientNoteActivity =>
  activityTypeMap.note.includes(activity.activityType);

export const isPatientPsychiatryActivity = (activity: PatientActivity): activity is PatientPsychiatryAppointmentActivity =>
  activityTypeMap.psychiatrySession.includes(activity.activityType);

export const isPatientTherapyActivity = (activity: PatientActivity): activity is PatientTherapyAppointmentActivity =>
  activityTypeMap.therapySession.includes(activity.activityType);

export const isPartnerReferralActivity = (activity: PatientActivity): activity is PartnerReferralActivity =>
  activityTypeMap.referral.includes(activity.activityType);

export type PatientActivityOutput = {
  data: PatientActivity[];
  cursor: string;
};

export const getPatientActivity = async (userId: string, data: { cursor?: string }) =>
  await restClient.request<PatientActivityOutput>(`/api/v1/patient/${userId}/activity`, {
    params: data,
  });
